import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    ChevronLeft,
    ChevronRight,
    Package,
    FileText,
    GraduationCap,
    MapPin,
    ShieldCheck,
    Star,
    Clock,
} from "lucide-react";

const SLIDES = [
    {
        kicker: "Benvenuti in NSP · Noleggio mezzi da cantiere",
        title: (
            <>
                Una flotta intera,
                <br />
                <span className="text-nsp-yellow-dark">pronta per te.</span>
            </>
        ),
        text: "Muletti, miniescavatori, demolitori, PLE e gruppi elettrogeni: 60+ mezzi certificati e pronti alla consegna in cantiere.",
        image: "/hero/flotta-destra.png",
        light: true,
        cta: { label: "Esplora il catalogo", to: "/catalogo", icon: Package },
        secondary: { label: "Calcola preventivo", to: "/preventivo" },
    },
    {
        kicker: "Preventivo · Istantaneo e trasparente",
        title: (
            <>
                Il tuo preventivo,
                <br />
                <span className="text-nsp-yellow">in un attimo.</span>
            </>
        ),
        text: "Scegli i mezzi, le date e i servizi extra: calcolo immediato con IVA, codici sconto e PDF pronto da scaricare.",
        image: "/hero/preventivo-calcolo.png",
        cta: { label: "Calcola preventivo", to: "/preventivo", icon: FileText },
        secondary: { label: "Sfoglia catalogo", to: "/catalogo" },
    },
    {
        kicker: "Formazione · D.Lgs. 81/08",
        title: (
            <>
                Corsi e patentini
                <br />
                <span className="text-nsp-yellow">per operatori.</span>
            </>
        ),
        text: "Abilitazioni per escavatori, caricatori, terne, gru per scarrabili e ponteggi. Attestati riconosciuti e docenti qualificati.",
        image: "https://images.unsplash.com/photo-1626885930974-4b69aa21bbf9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjB0cmFpbmluZyUyMGNsYXNzfGVufDB8fHx8MTc4NDAzMTg2NXww&ixlib=rb-4.1.0&q=85",
        cta: { label: "Scopri i corsi", to: "/corsi", icon: GraduationCap },
        secondary: { label: "Richiedi info", to: "/contatti" },
    },
    {
        kicker: "La nostra sede · Poggio Moiano (RI)",
        title: (
            <>
                Vieni a trovarci,
                <br />
                <span className="text-nsp-yellow">ti aspettiamo.</span>
            </>
        ),
        text: "Via Salaria Vecchia 31, Osteria Nuova di Poggio Moiano. Consegna e ritiro in tutta la Sabina con trasporto su preventivo.",
        image: "https://images.unsplash.com/photo-1758789667762-56175fe4601c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBleHRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3ODQwMzE4NjV8MA&ixlib=rb-4.1.0&q=85",
        cta: { label: "Contatti e mappa", to: "/contatti", icon: MapPin },
        secondary: { label: "Chatta con noi", to: "/contatti" },
    },
];

const AUTOPLAY_MS = 6000;

export const HeroCarousel = () => {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const touchX = useRef(null);

    const goTo = useCallback((i) => {
        setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
    }, []);

    useEffect(() => {
        if (paused) return undefined;
        const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), AUTOPLAY_MS);
        return () => clearInterval(t);
    }, [paused]);

    const onTouchStart = (e) => {
        touchX.current = e.touches[0].clientX;
        setPaused(true);
    };
    const onTouchEnd = (e) => {
        if (touchX.current !== null) {
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 50) goTo(index + (dx < 0 ? 1 : -1));
        }
        touchX.current = null;
        setPaused(false);
    };

    const slide = SLIDES[index];

    return (
        <section
            data-testid="hero-carousel"
            className="relative bg-gradient-to-br from-white via-white to-nsp-green-50"
        >
            <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-14 pt-4 md:pt-8 pb-10 md:pb-16">
                <div
                    className="relative rounded-[2rem] overflow-hidden shadow-2xl shadow-nsp-green/15 ring-1 ring-gray-200 flex flex-col sm:block sm:h-[520px] lg:h-[600px]"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Image layers (crossfade) */}
                    <div className="relative h-60 sm:h-full sm:absolute sm:inset-0">
                        {SLIDES.map((s, i) => (
                            <div
                                key={i}
                                data-testid={`hero-slide-${i}`}
                                aria-hidden={i !== index}
                                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                                    s.light ? "bg-white" : ""
                                } ${i === index ? "opacity-100 z-[1]" : "opacity-0 z-0"}`}
                            >
                                {s.light ? (
                                    <>
                                        <img
                                            src="/hero/flotta-mobile.png"
                                            alt=""
                                            className="sm:hidden w-full h-full object-contain bg-white"
                                            loading={i === 0 ? "eager" : "lazy"}
                                        />
                                        <img
                                            src={s.image}
                                            alt=""
                                            className="hidden sm:block w-full h-full object-cover object-right bg-white"
                                            loading={i === 0 ? "eager" : "lazy"}
                                        />
                                    </>
                                ) : (
                                    <img
                                        src={s.image}
                                        alt=""
                                        className={`w-full h-full object-cover transition-transform duration-[7000ms] ease-out ${
                                            i === index ? "scale-105" : "scale-100"
                                        }`}
                                        loading={i === 0 ? "eager" : "lazy"}
                                    />
                                )}
                                <div
                                    className={`absolute inset-0 ${
                                        s.light
                                            ? "bg-[linear-gradient(to_top,rgba(255,255,255,0.85)_0%,rgba(255,255,255,0)_30%,rgba(226,232,240,0)_55%,rgba(226,232,240,0.75)_100%)]"
                                            : "bg-gradient-to-t from-nsp-green via-nsp-green/30 to-black/10 sm:from-nsp-green/95 sm:via-nsp-green/40"
                                    }`}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Text content */}
                    <div
                        className={`relative z-[2] flex flex-col justify-end p-6 sm:p-10 lg:p-14 sm:absolute sm:inset-0 transition-colors duration-500 ${
                            slide.light ? "bg-white" : "bg-nsp-green"
                        } sm:!bg-transparent`}
                    >
                        <div key={index} className={`hero-slide-enter ${slide.light ? "max-w-xl" : "max-w-2xl"}`}>
                            <div
                                className={`inline-flex items-center gap-2 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] rounded-full backdrop-blur-md border ${
                                    slide.light
                                        ? "bg-nsp-green-50/90 border-nsp-green-100 text-nsp-green"
                                        : "bg-white/15 border-white/20 text-white"
                                }`}
                            >
                                <span
                                    className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                                        slide.light ? "bg-nsp-green-vivid" : "bg-nsp-yellow"
                                    }`}
                                />
                                {slide.kicker}
                            </div>
                            <h1
                                data-testid="hero-title"
                                className={`mt-5 font-display uppercase leading-[0.95] tracking-tight ${
                                    slide.light
                                        ? "text-3xl sm:text-5xl lg:text-6xl text-nsp-green"
                                        : "text-4xl sm:text-5xl lg:text-6xl text-white"
                                }`}
                            >
                                {slide.title}
                            </h1>
                            <p
                                className={`mt-4 text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl ${
                                    slide.light ? "text-gray-600" : "text-white/85"
                                }`}
                            >
                                {slide.text}
                            </p>
                            <div className="mt-7 flex flex-wrap gap-3">
                                <Link
                                    to={slide.cta.to}
                                    data-testid="hero-cta-primary"
                                    className={`group rounded-full px-7 py-4 font-bold text-sm inline-flex items-center gap-2 shadow-lg active:scale-95 transition-colors ${
                                        slide.light
                                            ? "bg-nsp-green text-white hover:bg-nsp-green-800"
                                            : "bg-nsp-yellow text-nsp-green hover:bg-white"
                                    }`}
                                >
                                    <slide.cta.icon className="w-4 h-4" strokeWidth={2.5} />
                                    {slide.cta.label}
                                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                                </Link>
                                <Link
                                    to={slide.secondary.to}
                                    data-testid="hero-cta-secondary"
                                    className={`rounded-full backdrop-blur-md border px-7 py-4 font-bold text-sm inline-flex items-center active:scale-95 transition-colors ${
                                        slide.light
                                            ? "bg-white/70 border-nsp-green/25 text-nsp-green hover:bg-nsp-green hover:text-white"
                                            : "bg-white/10 border-white/30 text-white hover:bg-white hover:text-nsp-green"
                                    }`}
                                >
                                    {slide.secondary.label}
                                </Link>
                            </div>
                        </div>

                        {/* Controls row */}
                        <div className="mt-8 flex items-center justify-between">
                            {/* Dots + progress */}
                            <div className="flex items-center gap-2">
                                {SLIDES.map((_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        data-testid={`hero-carousel-dot-${i}`}
                                        aria-label={`Vai alla slide ${i + 1}`}
                                        onClick={() => goTo(i)}
                                        className={`relative h-2 rounded-full overflow-hidden transition-[width,background-color] duration-500 ${
                                            i === index
                                                ? `w-10 ${slide.light ? "bg-nsp-green/15" : "bg-white/30"}`
                                                : slide.light
                                                  ? "w-2 bg-nsp-green/25 hover:bg-nsp-green/50"
                                                  : "w-2 bg-white/40 hover:bg-white/70"
                                        }`}
                                    >
                                        {i === index && (
                                            <span
                                                key={`p-${index}`}
                                                className={`absolute inset-y-0 left-0 rounded-full hero-dot-progress ${
                                                    slide.light ? "bg-nsp-green" : "bg-nsp-yellow"
                                                }`}
                                                style={{ animationDuration: `${AUTOPLAY_MS}ms`, animationPlayState: paused ? "paused" : "running" }}
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* Arrows */}
                            <div className="hidden sm:flex items-center gap-2">
                                {[
                                    { dir: -1, testid: "hero-carousel-prev", label: "Slide precedente", Icon: ChevronLeft },
                                    { dir: 1, testid: "hero-carousel-next", label: "Slide successiva", Icon: ChevronRight },
                                ].map(({ dir, testid, label, Icon }) => (
                                    <button
                                        key={testid}
                                        type="button"
                                        data-testid={testid}
                                        aria-label={label}
                                        onClick={() => goTo(index + dir)}
                                        className={`w-11 h-11 rounded-full backdrop-blur-md border flex items-center justify-center active:scale-95 transition-colors ${
                                            slide.light
                                                ? "bg-white/70 border-nsp-green/25 text-nsp-green hover:bg-nsp-green hover:text-white"
                                                : "bg-white/10 border-white/25 text-white hover:bg-white hover:text-nsp-green"
                                        }`}
                                    >
                                        <Icon className="w-5 h-5" strokeWidth={2.5} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trust badges under carousel */}
                <div
                    data-testid="hero-trust-badges"
                    className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-gray-500"
                >
                    <TrustBadge icon={ShieldCheck} label="A norma D.Lgs. 81/08" />
                    <TrustBadge icon={Star} label="60+ attrezzature" />
                    <TrustBadge icon={Clock} label="Chat AI 24/7" />
                </div>
            </div>
        </section>
    );
};

const TrustBadge = ({ icon: Icon, label }) => (
    <div className="inline-flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-nsp-green-50 flex items-center justify-center flex-shrink-0">
            <Icon className="w-3 h-3 text-nsp-green" strokeWidth={2.5} />
        </div>
        <span className="font-semibold text-gray-600">{label}</span>
    </div>
);

export default HeroCarousel;
